import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Mic, Zap, FileText } from 'lucide-react';
import LiteShell, { StepIndicator } from '../LiteShell';
import DeIdPanel from '@/components/lite/DeIdPanel';
import OutputView from '@/components/lite/OutputView';
import RateLimitGate from '@/components/lite/RateLimitGate';
import DictationError, { IframeDictationNotice } from '@/components/lite/DictationError';
import useDictation from '@/hooks/useDictation';
import { liteApi } from '@/utils/lite/liteApi';

const LENGTH_OPTIONS = [
  { value: 'shorter', label: 'Shorter' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'simpler', label: 'Even simpler language' },
];

export default function ParentSummary() {
  const [step, setStep] = useState(1);
  const [inputMode, setInputMode] = useState('quick'); // 'quick' | 'paste'
  const [quickInputs, setQuickInputs] = useState({
    focus: '',          // What we worked on
    strengths: '',      // Wins
    challenges: '',     // Hard moments
    next: '',           // Next steps
    homeIdeas: '',      // Optional home ideas seed
  });
  const [pastedNotes, setPastedNotes] = useState('');

  // Dictation hook — handles errors visibly, iframe detection, auto-stop
  const {
    recordingField,
    error: dictationError,
    supported: voiceSupported,
    inIframe: dictationInIframe,
    start: startDictation,
    dismissError: dismissDictationError,
  } = useDictation({
    onTranscript: (fieldKey, transcript) => {
      if (fieldKey === 'paste') {
        setPastedNotes((p) => (p ? p + ' ' : '') + transcript);
      } else {
        setQuickInputs((q) => ({ ...q, [fieldKey]: (q[fieldKey] ? q[fieldKey] + ' ' : '') + transcript }));
      }
    },
    maxSeconds: 30,
  });

  const [form, setForm] = useState({
    include_strengths: true,
    include_challenges: true,
    include_strategies: true,
    include_next_steps: true,
    length_mode: 'balanced',
  });
  const [cleanedNotes, setCleanedNotes] = useState('');
  const [output, setOutput] = useState('');
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [gateOpen, setGateOpen] = useState(false);
  const [gateInfo, setGateInfo] = useState({ used: 0, limit: 0 });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateQuick = (k, v) => setQuickInputs((q) => ({ ...q, [k]: v }));

  // Build the consolidated notes text from whichever mode is active.
  const buildNotesText = () => {
    if (inputMode === 'paste') return pastedNotes.trim();
    const lines = [];
    if (quickInputs.focus.trim()) lines.push(`Focus / what we worked on: ${quickInputs.focus.trim()}`);
    if (quickInputs.strengths.trim()) lines.push(`Strengths / wins: ${quickInputs.strengths.trim()}`);
    if (quickInputs.challenges.trim()) lines.push(`Challenges / hard moments: ${quickInputs.challenges.trim()}`);
    if (quickInputs.next.trim()) lines.push(`Next steps: ${quickInputs.next.trim()}`);
    if (quickInputs.homeIdeas.trim()) lines.push(`Home practice ideas the SLP wants included: ${quickInputs.homeIdeas.trim()}`);
    return lines.join('\n\n');
  };

  // Voice dictation handled by useDictation hook above

  const goToDeId = () => {
    setError('');
    const built = buildNotesText();
    if (!built || built.length < 30) {
      setError('Please add at least a sentence about the session (any field counts).');
      return;
    }
    setStep(2);
  };

  const proceedAfterDeId = (cleaned) => {
    setCleanedNotes(cleaned);
    setStep(3);
    runGenerate(cleaned);
  };

  const runGenerate = async (notesText) => {
    setOutput('');
    setError('');
    try {
      let email = null;
      try { email = localStorage.getItem('playfirst_lite_email'); } catch {}
      const payload = {
        notes: notesText,
        include_strengths: form.include_strengths,
        include_challenges: form.include_challenges,
        include_strategies: form.include_strategies,
        include_next_steps: form.include_next_steps,
        length_mode: form.length_mode,
        confirm_deidentified: true,
        email,
      };
      const res = await liteApi.generateParent(payload);
      setOutput(res.output);
      setUsage({ used_today: res.used_today, daily_limit: res.daily_limit });
      setStep(4);
    } catch (e) {
      if (e.status === 429) {
        const d = e.data?.detail || {};
        setGateInfo({ used: d.used || 0, limit: d.limit || 5 });
        setGateOpen(true);
        setStep(1);
      } else {
        setError(e.message || 'Generation failed.');
      }
    }
  };

  return (
    <LiteShell currentStep={step}>
      <h1 className="text-3xl font-serif text-primary-900 mb-2">Parent Summary</h1>
      <p className="text-stone-500 mb-6">
        Quick, warm parent updates without retyping your SOAP notes. Use a few quick prompts, paste from your EMR, or dictate.
      </p>
      <StepIndicator step={step} />

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="lite-parent-error">{error}</div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          <IframeDictationNotice inIframe={dictationInIframe} />
          <DictationError error={dictationError} inIframe={dictationInIframe} onDismiss={dismissDictationError} />
          <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full sm:w-auto sm:inline-flex">
              <TabsTrigger value="quick" data-testid="parent-tab-quick">
                <Zap className="w-3.5 h-3.5 mr-1.5" /> Quick fields
              </TabsTrigger>
              <TabsTrigger value="paste" data-testid="parent-tab-paste">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Paste notes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="quick" className="space-y-4 mt-4">
              <p className="text-sm text-stone-500">
                Just a few words in any field — we'll handle the rest. Skip what you don't need.
              </p>
              <QuickField
                label="Focus / what we worked on"
                placeholder="e.g. core vocabulary during bubble play"
                value={quickInputs.focus}
                onChange={(v) => updateQuick('focus', v)}
                onDictate={() => startDictation('focus')}
                recording={recordingField === 'focus'}
                voiceSupported={voiceSupported}
                testId="parent-q-focus"
              />
              <QuickField
                label="Strengths / wins"
                placeholder="e.g. used a 2-word combination spontaneously"
                value={quickInputs.strengths}
                onChange={(v) => updateQuick('strengths', v)}
                onDictate={() => startDictation('strengths')}
                recording={recordingField === 'strengths'}
                voiceSupported={voiceSupported}
                testId="parent-q-strengths"
              />
              <QuickField
                label="Challenges / hard moments"
                placeholder="e.g. dysregulated during transitions"
                value={quickInputs.challenges}
                onChange={(v) => updateQuick('challenges', v)}
                onDictate={() => startDictation('challenges')}
                recording={recordingField === 'challenges'}
                voiceSupported={voiceSupported}
                testId="parent-q-challenges"
              />
              <QuickField
                label="Next steps"
                placeholder="e.g. continue scripting from preferred shows, expand to 3-word combos"
                value={quickInputs.next}
                onChange={(v) => updateQuick('next', v)}
                onDictate={() => startDictation('next')}
                recording={recordingField === 'next'}
                voiceSupported={voiceSupported}
                testId="parent-q-next"
              />
              <QuickField
                label="Home practice ideas (optional)"
                placeholder="e.g. encourage scripts during dinner routines"
                value={quickInputs.homeIdeas}
                onChange={(v) => updateQuick('homeIdeas', v)}
                onDictate={() => startDictation('homeIdeas')}
                recording={recordingField === 'homeIdeas'}
                voiceSupported={voiceSupported}
                testId="parent-q-home"
              />
            </TabsContent>

            <TabsContent value="paste" className="space-y-2 mt-4">
              <div className="flex items-center justify-between">
                <Label>Paste your SOAP notes (de-identified)</Label>
                {voiceSupported && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => startDictation('paste')}
                    className={`rounded-full h-7 ${recordingField === 'paste' ? 'text-red-600' : 'text-stone-500'}`}
                    data-testid="parent-paste-dictate"
                  >
                    <Mic className="w-3.5 h-3.5 mr-1" />
                    {recordingField === 'paste' ? 'Listening…' : 'Dictate'}
                  </Button>
                )}
              </div>
              <p className="text-sm text-stone-500 mb-2">
                Copy directly from your EMR. Names will be flagged before generation.
              </p>
              <Textarea
                data-testid="parent-notes-textarea"
                placeholder="Paste SOAP notes, observations, anything from the session."
                value={pastedNotes}
                onChange={(e) => setPastedNotes(e.target.value)}
                rows={10}
              />
            </TabsContent>
          </Tabs>

          <div className="grid sm:grid-cols-2 gap-3 p-4 rounded-xl bg-stone-50 border border-stone-100">
            <CheckboxRow id="strengths" label="Include strengths" checked={form.include_strengths} onChange={(v) => update('include_strengths', v)} testId="parent-include-strengths" />
            <CheckboxRow id="challenges" label="Include challenges" checked={form.include_challenges} onChange={(v) => update('include_challenges', v)} testId="parent-include-challenges" />
            <CheckboxRow id="strategies" label="Include strategies" checked={form.include_strategies} onChange={(v) => update('include_strategies', v)} testId="parent-include-strategies" />
            <CheckboxRow id="next-steps" label="Include next steps" checked={form.include_next_steps} onChange={(v) => update('include_next_steps', v)} testId="parent-include-nextsteps" />
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Length / readability</Label>
              <Select value={form.length_mode} onValueChange={(v) => update('length_mode', v)}>
                <SelectTrigger data-testid="parent-length-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LENGTH_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={goToDeId} className="rounded-full" data-testid="parent-next-btn">Next: Clean &amp; review</Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <DeIdPanel text={buildNotesText()} onProceed={proceedAfterDeId} onCancel={() => setStep(1)} />
      )}

      {step === 3 && (
        <div className="flex flex-col items-center justify-center py-16 text-stone-600" data-testid="lite-generating">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
          <p className="font-medium">Crafting your parent summary…</p>
          <p className="text-sm text-stone-500 mt-1">This usually takes 5–15 seconds.</p>
        </div>
      )}

      {step === 4 && output && (
        <OutputView
          output={output}
          toolLabel="Parent Summary"
          tool="parent"
          usage={usage}
          onRegenerate={() => runGenerate(cleanedNotes)}
          onStartOver={() => { setOutput(''); setStep(1); }}
          onWaitlist={() => { setGateInfo({ used: 0, limit: 0 }); setGateOpen(true); }}
        />
      )}

      <RateLimitGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        used={gateInfo.used}
        limit={gateInfo.limit}
        onUnlocked={() => setGateOpen(false)}
      />
    </LiteShell>
  );
}

function QuickField({ label, placeholder, value, onChange, onDictate, recording, voiceSupported, testId }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {voiceSupported && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDictate}
            className={`h-6 px-2 rounded-full text-xs ${recording ? 'text-red-600 bg-red-50' : 'text-stone-500'}`}
            data-testid={`${testId}-dictate`}
          >
            <Mic className={`w-3 h-3 mr-1 ${recording ? 'animate-pulse' : ''}`} />
            {recording ? 'Listening…' : 'Dictate'}
          </Button>
        )}
      </div>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

function CheckboxRow({ id, label, checked, onChange, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testId} />
      <Label htmlFor={id} className="cursor-pointer">{label}</Label>
    </div>
  );
}
